import './styles/app.css';
import { DraftStore } from './core/drafts';
import { generateMap } from './core/generator';
import { clamp, neighbours } from './core/iso';
import { ApiError, RestClient } from './core/rest';
import { BUILTIN_ASSETS } from './core/builtin-assets';
import { WorldRenderer } from './core/renderer';
import {
  DEFAULT_TARGETS,
  TERRAIN_IDS,
  type AssetDefinition,
  type AssetManifest,
  type BootConfig,
  type GeneratorOptions,
  type MapDocument,
  type MapSummary,
  type PathId,
  type RuntimeConfig,
  type TerrainId,
  type ToolId,
} from './core/types';

declare global {
  interface Window {
    IFW_BOOT?: BootConfig;
    IsometricFantasyWorld?: {
      get(id: string): PublicWorldApi | undefined;
      instances: Map<string, PublicWorldApi>;
    };
  }
}

interface PublicWorldApi {
  highlightEntity(id: string, options?: Parameters<WorldRenderer['highlightEntity']>[1]): void;
  clearHighlight(id?: string): void;
  focusCell(cell: { x: number; y: number; z?: number }): void;
  selectEntity(id: string): void;
  moveEntity(id: string, path: Array<{ x: number; y: number; z?: number }>, options?: Parameters<WorldRenderer['moveEntity']>[2]): Promise<void>;
  getEntity(id: string): ReturnType<WorldRenderer['getEntity']>;
  getMapMetadata(): ReturnType<WorldRenderer['getMapMetadata']>;
}

const publicInstances = new Map<string, PublicWorldApi>();
window.IsometricFantasyWorld = { get: (id) => publicInstances.get(id), instances: publicInstances };

function bootConfigForRoot(root: HTMLElement): BootConfig | null {
  const fallback = window.IFW_BOOT;
  const restRoot = root.dataset.ifwRestRoot || fallback?.restRoot;
  if (!restRoot) return null;
  return {
    restRoot,
    nonce: root.dataset.ifwNonce ?? fallback?.nonce ?? '',
    pluginUrl: root.dataset.ifwPluginUrl ?? fallback?.pluginUrl ?? '',
    isLoggedIn: root.dataset.ifwLoggedIn ? root.dataset.ifwLoggedIn === '1' : Boolean(fallback?.isLoggedIn),
    currentUserId: Number(root.dataset.ifwUserId ?? fallback?.currentUserId ?? 0),
  };
}

function cloneDocument(document: MapDocument): MapDocument {
  return structuredClone ? structuredClone(document) : JSON.parse(JSON.stringify(document)) as MapDocument;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]!);
}

function randomSeed(): string {
  const themes = ['amber-coast', 'dragon-meadow', 'moonlit-river', 'old-kingdom', 'silver-forest'];
  return `${themes[Math.floor(Math.random() * themes.length)]}-${Math.floor(Math.random() * 99999)}`;
}

function normalizeTargets(formData: FormData): GeneratorOptions['targets'] {
  const values = { ...DEFAULT_TARGETS };
  let sum = 0;
  for (const id of TERRAIN_IDS) {
    values[id] = Math.max(0, Number(formData.get(`terrain-${id}`) ?? DEFAULT_TARGETS[id]));
    sum += values[id];
  }
  if (!sum) return { ...DEFAULT_TARGETS };
  for (const id of TERRAIN_IDS) values[id] = values[id] / sum * 100;
  return values;
}

function generateAsync(title: string, width: number, height: number, options: GeneratorOptions, stylePackVersion: number): Promise<MapDocument> {
  if (typeof Worker === 'undefined') return Promise.resolve(generateMap(title, width, height, options, stylePackVersion));
  return new Promise<MapDocument>((resolve, reject) => {
    const worker = new Worker(new URL('./core/generator.worker.ts', import.meta.url), { type: 'module' });
    const timeout = window.setTimeout(() => {
      worker.terminate();
      reject(new Error('Die Kartengenerierung hat zu lange gedauert.'));
    }, 30000);
    worker.onmessage = (event) => {
      clearTimeout(timeout);
      worker.terminate();
      if (event.data.ok) resolve(event.data.document as MapDocument);
      else reject(new Error(event.data.message));
    };
    worker.onerror = (event) => {
      clearTimeout(timeout);
      worker.terminate();
      reject(new Error(event.message));
    };
    worker.postMessage({ title, width, height, options, stylePackVersion });
  }).catch(() => generateMap(title, width, height, options, stylePackVersion));
}

class WorldApplication {
  private readonly api: RestClient;
  private readonly drafts = new DraftStore();
  private readonly mode: 'builder' | 'player';
  private readonly initialMap: string;
  private readonly controls: string;
  private runtime!: RuntimeConfig;
  private manifest!: AssetManifest;
  private renderer!: WorldRenderer;
  private document: MapDocument | null = null;
  private maps: MapSummary[] = [];
  private selectedTool: ToolId = 'terrain';
  private selectedTerrain: TerrainId = 'grass';
  private selectedPath: PathId = 'trail';
  private selectedAsset = 'builtin-tree';
  private history: string[] = [];
  private future: string[] = [];
  private strokeActive = false;
  private lastPainted = '';
  private dirty = false;
  private saveTimer = 0;
  private periodicTimer = 0;
  private shell!: HTMLElement;
  private viewport!: HTMLElement;
  private toolbar!: HTMLElement;
  private inspector!: HTMLElement;
  private status!: HTMLElement;

  constructor(private readonly root: HTMLElement, bootConfig: BootConfig) {
    this.api = new RestClient(bootConfig);
    this.mode = root.dataset.ifwMode === 'builder' ? 'builder' : 'player';
    this.initialMap = root.dataset.ifwMap ?? '';
    this.controls = root.dataset.ifwControls ?? 'compact';
  }

  async init(): Promise<void> {
    [this.runtime, this.manifest] = await Promise.all([this.api.config(), this.api.assets()]);
    this.buildShell();
    this.renderer = new WorldRenderer(this.viewport);
    await this.renderer.init(this.mode);
    this.renderer.onCell((x, y, kind) => this.onCell(x, y, kind));
    this.renderer.onObject((id) => this.onObject(id));
    this.registerPublicApi();

    if (this.mode === 'builder') {
      this.maps = await this.api.maps();
      this.updateMapSelect();
      if (this.initialMap && this.initialMap !== 'new') await this.loadMap(this.initialMap);
      else this.openNewMapDialog();
      this.periodicTimer = window.setInterval(() => { if (this.dirty) void this.save(); }, 60000);
    } else {
      if (!this.initialMap) throw new Error('Im Player-Shortcode fehlt die Map-UUID.');
      await this.loadMap(this.initialMap);
    }
    this.root.dispatchEvent(new CustomEvent('ifw:ready', { bubbles: true, detail: { id: this.root.id, map: this.document?.id } }));
  }

  private buildShell(): void {
    this.root.innerHTML = `
      <div class="ifw-shell ifw-mode-${this.mode} ifw-controls-${this.controls}">
        <header class="ifw-topbar">
          <div class="ifw-brand"><span class="ifw-brand-mark" aria-hidden="true">◆</span><span>Fantasy World</span></div>
          ${this.mode === 'builder' ? `
            <label class="ifw-map-picker"><span class="screen-reader-text">Map</span><select data-action="map-select" aria-label="Map auswählen"><option value="new">＋ Neue Map</option></select></label>
            <div class="ifw-top-actions">
              <button type="button" class="ifw-icon-button" data-action="undo" title="Rückgängig">↶</button>
              <button type="button" class="ifw-icon-button" data-action="redo" title="Wiederholen">↷</button>
              <button type="button" class="ifw-button ifw-save" data-action="save">Speichern</button>
              <button type="button" class="ifw-button ifw-button-secondary" data-action="publish">Veröffentlichen</button>
              <button type="button" class="ifw-icon-button ifw-more-toggle" data-action="toggle-inspector" title="Bibliothek">☰</button>
            </div>` : `<div class="ifw-player-title" data-role="title">Fantasy World</div>`}
        </header>
        <div class="ifw-workspace">
          ${this.mode === 'builder' ? '<aside class="ifw-toolbar" aria-label="Werkzeuge"></aside>' : ''}
          <main class="ifw-viewport" aria-label="Isometrische Karte"></main>
          ${this.mode === 'builder' ? '<aside class="ifw-inspector" aria-label="Bibliothek"></aside>' : ''}
          <div class="ifw-camera-controls" aria-label="Kamerasteuerung">
            <button type="button" data-camera="out" aria-label="Verkleinern">−</button>
            <button type="button" data-camera="fit" aria-label="Karte einpassen">⌂</button>
            <button type="button" data-camera="in" aria-label="Vergrößern">＋</button>
          </div>
        </div>
        <footer class="ifw-status" aria-live="polite">Bereit</footer>
        ${this.mode === 'builder' ? this.newMapDialogMarkup() : ''}
      </div>`;
    this.shell = this.root.querySelector('.ifw-shell')!;
    this.viewport = this.root.querySelector('.ifw-viewport')!;
    this.status = this.root.querySelector('.ifw-status')!;
    this.toolbar = this.root.querySelector('.ifw-toolbar') ?? document.createElement('div');
    this.inspector = this.root.querySelector('.ifw-inspector') ?? document.createElement('div');
    this.root.querySelector('[data-camera="in"]')?.addEventListener('click', () => this.renderer.zoomBy(1.25));
    this.root.querySelector('[data-camera="out"]')?.addEventListener('click', () => this.renderer.zoomBy(0.8));
    this.root.querySelector('[data-camera="fit"]')?.addEventListener('click', () => this.renderer.fit());
    if (this.mode === 'builder') this.bindBuilderUi();
  }

  private newMapDialogMarkup(): string {
    const targetInputs = TERRAIN_IDS.map((id) => `<label class="ifw-range-row"><span>${escapeHtml(this.manifest.terrain[id].label)}</span><input type="range" min="0" max="50" step="1" name="terrain-${id}" value="${DEFAULT_TARGETS[id]}"><output>${DEFAULT_TARGETS[id]}%</output></label>`).join('');
    return `<dialog class="ifw-dialog" data-role="new-map-dialog">
      <form method="dialog" data-form="new-map">
        <div class="ifw-dialog-heading"><div><span class="ifw-eyebrow">Neue Welt</span><h2>Landschaft erschaffen</h2><p>Ein Seed macht jede Generierung exakt reproduzierbar.</p></div><button value="cancel" class="ifw-dialog-close" aria-label="Schließen">×</button></div>
        <div class="ifw-new-grid">
          <section>
            <label class="ifw-field"><span>Name</span><input name="title" required value="Unbenannte Welt" maxlength="120"></label>
            <div class="ifw-field-pair"><label class="ifw-field"><span>Breite</span><input name="width" type="number" min="16" max="${this.runtime.maxMapSize}" value="48"></label><label class="ifw-field"><span>Höhe</span><input name="height" type="number" min="16" max="${this.runtime.maxMapSize}" value="48"></label></div>
            <label class="ifw-field"><span>Seed</span><span class="ifw-input-action"><input name="seed" value="${randomSeed()}"><button type="button" data-action="random-seed">↻</button></span></label>
            <label class="ifw-field"><span>Landform</span><select name="landform"><option value="island">Insel / Archipel</option><option value="continent">Kontinent / Region</option></select></label>
            <label class="ifw-range-row"><span>Relief</span><input name="heightIntensity" type="range" min="0" max="1" step="0.05" value="0.7"><output>70%</output></label>
          </section>
          <section><h3>Terrain-Zielwerte</h3><p class="ifw-help">Die Werte werden auf 100 % normalisiert; Geografie darf um ±3 Prozentpunkte abweichen.</p>${targetInputs}</section>
        </div>
        <div class="ifw-dialog-actions"><button value="cancel" class="ifw-button ifw-button-ghost">Abbrechen</button><button value="default" class="ifw-button" data-action="generate">Welt generieren</button></div>
      </form>
    </dialog>`;
  }

  private bindBuilderUi(): void {
    this.renderToolbar();
    this.renderInspector();
    this.root.querySelector('[data-action="save"]')?.addEventListener('click', () => void this.save());
    this.root.querySelector('[data-action="undo"]')?.addEventListener('click', () => this.undo());
    this.root.querySelector('[data-action="redo"]')?.addEventListener('click', () => this.redo());
    this.root.querySelector('[data-action="publish"]')?.addEventListener('click', () => void this.togglePublish());
    this.root.querySelector('[data-action="toggle-inspector"]')?.addEventListener('click', () => this.shell.classList.toggle('ifw-inspector-open'));
    this.root.querySelector<HTMLSelectElement>('[data-action="map-select"]')?.addEventListener('change', (event) => {
      const value = (event.currentTarget as HTMLSelectElement).value;
      if (value === 'new') this.openNewMapDialog(); else void this.loadMap(value);
    });
    const dialog = this.root.querySelector<HTMLDialogElement>('[data-role="new-map-dialog"]')!;
    const form = dialog.querySelector<HTMLFormElement>('[data-form="new-map"]')!;
    form.querySelector('[data-action="random-seed"]')?.addEventListener('click', () => { (form.elements.namedItem('seed') as HTMLInputElement).value = randomSeed(); });
    form.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => input.addEventListener('input', () => { const output = input.nextElementSibling; if (output) output.textContent = `${Math.round(Number(input.value) * (input.name === 'heightIntensity' ? 100 : 1))}%`; }));
    form.addEventListener('submit', (event) => {
      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
      if (submitter?.value === 'cancel') return;
      event.preventDefault();
      void this.createFromForm(form, dialog);
    });
    window.addEventListener('keydown', (event) => {
      if (!this.root.isConnected || ['INPUT', 'TEXTAREA', 'SELECT'].includes((event.target as HTMLElement).tagName)) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); void this.save(); }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); event.shiftKey ? this.redo() : this.undo(); }
      if (event.key === '1') this.selectTool('select');
      if (event.key === '2') this.selectTool('terrain');
      if (event.key === '3') this.selectTool('elevation-up');
      if (event.key === '4') this.selectTool('path');
      if (event.key === '5') this.selectTool('object');
      if (event.key === 'Backspace' || event.key === 'Delete') this.selectTool('erase');
    });
  }

  private renderToolbar(): void {
    const tools: Array<[ToolId, string, string]> = [
      ['select', '⌁', 'Auswählen (1)'], ['terrain', '◆', 'Terrain malen (2)'], ['fill', '◈', 'Fläche füllen'],
      ['elevation-up', '▲', 'Anheben (3)'], ['elevation-down', '▼', 'Absenken'], ['path', '╱', 'Weg (4)'],
      ['object', '♜', 'Objekt (5)'], ['erase', '⌫', 'Löschen'],
    ];
    this.toolbar.innerHTML = tools.map(([id, icon, title]) => `<button type="button" class="ifw-tool ${id === this.selectedTool ? 'is-active' : ''}" data-tool="${id}" title="${title}" aria-label="${title}"><span>${icon}</span><small>${title.replace(/ \(.+\)/, '')}</small></button>`).join('');
    this.toolbar.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach((button) => button.addEventListener('click', () => this.selectTool(button.dataset.tool as ToolId)));
  }

  private renderInspector(selectedObjectId?: string): void {
    const terrainButtons = TERRAIN_IDS.map((id) => `<button type="button" class="ifw-swatch ${this.selectedTerrain === id ? 'is-active' : ''}" data-terrain="${id}"><span style="--swatch:${this.manifest.terrain[id].color}"></span><small>${escapeHtml(this.manifest.terrain[id].label)}</small></button>`).join('');
    const pathButtons = Object.values(this.manifest.paths).map((path) => `<button type="button" class="ifw-list-choice ${this.selectedPath === path.id ? 'is-active' : ''}" data-path="${path.id}"><span style="--swatch:${path.color}"></span>${escapeHtml(path.label)}</button>`).join('');
    const assets = [...BUILTIN_ASSETS, ...this.manifest.objects];
    const assetButtons = assets.map((asset) => `<button type="button" class="ifw-asset-choice ${this.selectedAsset === asset.uuid ? 'is-active' : ''}" data-asset="${asset.uuid}">${asset.thumbnailUrl ? `<img src="${escapeHtml(asset.thumbnailUrl)}" alt="">` : `<span class="ifw-asset-icon">${this.assetIcon(asset)}</span>`}<span>${escapeHtml(asset.name)}</span><small>${asset.footprint.width}×${asset.footprint.height}</small></button>`).join('');
    const selected = selectedObjectId ? this.document?.objects.find((object) => object.id === selectedObjectId) : undefined;
    this.inspector.innerHTML = `<div class="ifw-inspector-head"><div><span class="ifw-eyebrow">Bibliothek</span><h2>${selected ? 'Auswahl' : 'Bauelemente'}</h2></div><button type="button" data-action="close-inspector" aria-label="Schließen">×</button></div>
      ${selected ? this.selectedObjectMarkup(selected) : `<div class="ifw-tabs" role="tablist"><button class="is-active" data-tab="terrain">Terrain</button><button data-tab="paths">Wege</button><button data-tab="objects">Objekte</button></div>
      <section data-panel="terrain"><div class="ifw-swatch-grid">${terrainButtons}</div></section>
      <section data-panel="paths" hidden><div class="ifw-choice-list">${pathButtons}</div></section>
      <section data-panel="objects" hidden><div class="ifw-asset-grid">${assetButtons}</div></section>`}`;
    this.inspector.querySelector('[data-action="close-inspector"]')?.addEventListener('click', () => this.shell.classList.remove('ifw-inspector-open'));
    this.inspector.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((button) => button.addEventListener('click', () => {
      this.inspector.querySelectorAll('[data-tab]').forEach((tab) => tab.classList.toggle('is-active', tab === button));
      this.inspector.querySelectorAll<HTMLElement>('[data-panel]').forEach((panel) => { panel.hidden = panel.dataset.panel !== button.dataset.tab; });
    }));
    this.inspector.querySelectorAll<HTMLButtonElement>('[data-terrain]').forEach((button) => button.addEventListener('click', () => { this.selectedTerrain = button.dataset.terrain as TerrainId; this.selectTool('terrain'); this.renderInspector(); }));
    this.inspector.querySelectorAll<HTMLButtonElement>('[data-path]').forEach((button) => button.addEventListener('click', () => { this.selectedPath = button.dataset.path as PathId; this.selectTool('path'); this.renderInspector(); }));
    this.inspector.querySelectorAll<HTMLButtonElement>('[data-asset]').forEach((button) => button.addEventListener('click', () => { this.selectedAsset = button.dataset.asset!; this.selectTool('object'); this.renderInspector(); }));
    this.inspector.querySelector('[data-action="delete-selected"]')?.addEventListener('click', () => {
      if (!selected) return;
      this.pushHistory();
      this.document!.objects = this.document!.objects.filter((object) => object.id !== selected.id);
      this.markDirty();
      void this.renderer.setWorld(this.document!, this.manifest, false);
      this.renderInspector();
    });
    this.inspector.querySelector('[data-action="duplicate-selected"]')?.addEventListener('click', () => {
      if (!selected || !this.document) return;
      this.pushHistory();
      const copy = cloneDocument(this.document).objects.find((object) => object.id === selected.id)!;
      copy.id = crypto.randomUUID(); copy.x = clamp(copy.x + 1, 0, this.document.width - 1); copy.y = clamp(copy.y + 1, 0, this.document.height - 1);
      this.document.objects.push(copy); this.markDirty(); void this.renderer.setWorld(this.document, this.manifest, false);
    });
    this.inspector.querySelector('[data-action="export"]')?.addEventListener('click', () => { if (this.document?.revision) void this.api.exportMap(this.document.id); });
  }

  private selectedObjectMarkup(object: MapDocument['objects'][number]): string {
    const asset = [...BUILTIN_ASSETS, ...this.manifest.objects].find((entry) => entry.uuid === object.assetId);
    return `<div class="ifw-selection-card"><div class="ifw-asset-large">${asset?.thumbnailUrl ? `<img src="${escapeHtml(asset.thumbnailUrl)}" alt="">` : this.assetIcon(asset ?? BUILTIN_ASSETS[0]!)}</div><h3>${escapeHtml(asset?.name ?? object.assetId)}</h3><dl><div><dt>Position</dt><dd>${object.x}, ${object.y}, ${object.z}</dd></div><div><dt>Footprint</dt><dd>${object.footprint.width}×${object.footprint.height}</dd></div><div><dt>Instanz-ID</dt><dd><code>${escapeHtml(object.id)}</code></dd></div></dl><button type="button" class="ifw-button ifw-button-secondary" data-action="duplicate-selected">Duplizieren</button><button type="button" class="ifw-button ifw-button-danger" data-action="delete-selected">Löschen</button><hr><button type="button" class="ifw-button ifw-button-ghost" data-action="export">IFW-Paket exportieren</button></div>`;
  }

  private assetIcon(asset: AssetDefinition): string {
    return ({ building: '⌂', vegetation: '♠', character: '♟', creature: '♞', vehicle: '◒', prop: '◉' } as Record<string, string>)[asset.type] ?? '◆';
  }

  private selectTool(tool: ToolId): void {
    this.selectedTool = tool;
    this.renderToolbar();
    if (window.matchMedia('(max-width: 760px)').matches && ['terrain', 'path', 'object'].includes(tool)) this.shell.classList.add('ifw-inspector-open');
    this.setStatus(`Werkzeug: ${tool}`);
  }

  private async createFromForm(form: HTMLFormElement, dialog: HTMLDialogElement): Promise<void> {
    const formData = new FormData(form);
    const width = clamp(Number(formData.get('width')), 16, this.runtime.maxMapSize);
    const height = clamp(Number(formData.get('height')), 16, this.runtime.maxMapSize);
    const options: GeneratorOptions = {
      seed: String(formData.get('seed') || randomSeed()), targets: normalizeTargets(formData),
      heightIntensity: clamp(Number(formData.get('heightIntensity')), 0, 1),
      landform: formData.get('landform') === 'continent' ? 'continent' : 'island',
    };
    this.setStatus('Geografie wird erzeugt…', true);
    const document = await generateAsync(String(formData.get('title') || 'Unbenannte Welt'), width, height, options, this.manifest.stylePackVersion);
    this.document = document;
    this.history = []; this.future = []; this.dirty = true;
    await this.renderer.setWorld(document, this.manifest, true);
    dialog.close();
    this.markDirty();
    this.setStatus(`${width}×${height} Welt aus Seed „${options.seed}“ erzeugt.`);
  }

  private openNewMapDialog(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>('[data-role="new-map-dialog"]');
    if (dialog && !dialog.open) dialog.showModal();
  }

  private async loadMap(uuid: string): Promise<void> {
    this.setStatus('Map wird geladen…', true);
    const serverDocument = await this.api.getMap(uuid);
    if (serverDocument.stylePackVersion !== this.manifest.stylePackVersion) {
      this.manifest = await this.api.assets(serverDocument.stylePackVersion);
      this.renderInspector();
    }
    const local = await this.drafts.load(uuid);
    this.document = local && local.document.revision === serverDocument.revision && JSON.stringify(local.document) !== JSON.stringify(serverDocument) && window.confirm('Es gibt einen nicht gespeicherten lokalen Entwurf. Wiederherstellen?') ? local.document : serverDocument;
    this.history = []; this.future = []; this.dirty = this.document !== serverDocument;
    await this.renderer.setWorld(this.document, this.manifest, true);
    const title = this.root.querySelector('[data-role="title"]'); if (title) title.textContent = this.document.title;
    this.updatePublishButton();
    this.updateMapSelect();
    this.setStatus(`${this.document.title} · Revision ${this.document.revision}`);
  }

  private onCell(x: number, y: number, kind: 'start' | 'move' | 'end'): void {
    if (!this.document || this.mode !== 'builder') return;
    const key = `${x}:${y}`;
    if (kind === 'start') { this.strokeActive = true; this.lastPainted = ''; this.pushHistory(); }
    if (kind === 'end') { this.strokeActive = false; this.lastPainted = ''; return; }
    if (!this.strokeActive || this.lastPainted === key) return;
    this.lastPainted = key;
    const index = y * this.document.width + x;
    const cell = this.document.cells[index]!;
    let changed = false;
    switch (this.selectedTool) {
      case 'select': {
        const object = this.renderer.objectAt(x, y);
        if (object) this.onObject(object.id); else this.setStatus(`Zelle ${x}, ${y} · ${cell.terrain} · Höhe ${cell.elevation}`);
        return;
      }
      case 'terrain':
        cell.terrain = this.selectedTerrain; if (this.selectedTerrain.includes('water')) { cell.elevation = 0; cell.path = null; } changed = true; break;
      case 'fill':
        this.floodFill(x, y, cell.terrain, this.selectedTerrain); changed = true; this.strokeActive = false; break;
      case 'elevation-up':
        if (!cell.terrain.includes('water')) { const adjacent = neighbours(x, y, this.document.width, this.document.height).map((point) => this.document!.cells[point.y * this.document!.width + point.x]!).filter((entry) => !entry.terrain.includes('water')); cell.elevation = Math.min(3, cell.elevation + 1, adjacent.length ? Math.min(...adjacent.map((entry) => entry.elevation + 1)) : 3); changed = true; } break;
      case 'elevation-down':
        if (!cell.terrain.includes('water')) { const adjacent = neighbours(x, y, this.document.width, this.document.height).map((point) => this.document!.cells[point.y * this.document!.width + point.x]!).filter((entry) => !entry.terrain.includes('water')); const floor = adjacent.length ? Math.max(...adjacent.map((entry) => entry.elevation - 1)) : 0; cell.elevation = Math.max(0, floor, cell.elevation - 1); changed = true; } break;
      case 'path':
        if (!cell.terrain.includes('water')) { cell.path = cell.path === this.selectedPath ? null : this.selectedPath; changed = true; } break;
      case 'object':
        changed = Boolean(this.placeObject(this.selectedAsset, x, y)); this.strokeActive = false; break;
      case 'erase': {
        if (this.renderer.removeObjectAt(x, y)) changed = true;
        else if (cell.path) { cell.path = null; changed = true; }
        else { cell.terrain = 'grass'; cell.elevation = 0; changed = true; }
        break;
      }
    }
    if (changed) { this.markDirty(); void this.renderer.setWorld(this.document, this.manifest, false); }
  }

  private placeObject(assetId: string, x: number, y: number): boolean {
    if (!this.document) return false;
    const asset = [...BUILTIN_ASSETS, ...this.manifest.objects].find((entry) => entry.uuid === assetId);
    if (!asset || x + asset.footprint.width > this.document.width || y + asset.footprint.height > this.document.height) { this.setStatus('Das Objekt passt hier nicht auf die Map.', false, true); return false; }
    const cells = [];
    for (let oy = 0; oy < asset.footprint.height; oy += 1) for (let ox = 0; ox < asset.footprint.width; ox += 1) cells.push(this.document.cells[(y + oy) * this.document.width + x + ox]!);
    if (cells.some((cell) => cell.terrain.includes('water')) || new Set(cells.map((cell) => cell.elevation)).size > 1 || cells.some((_cell, index) => this.renderer.objectAt(x + index % asset.footprint.width, y + Math.floor(index / asset.footprint.width)))) {
      this.setStatus('Objekte benötigen eine freie, ebene und trockene Standfläche.', false, true); return false;
    }
    this.renderer.addObject(assetId, x, y); return true;
  }

  private floodFill(startX: number, startY: number, from: TerrainId, to: TerrainId): void {
    if (!this.document || from === to) return;
    const queue = [{ x: startX, y: startY }]; const seen = new Set<string>();
    while (queue.length && seen.size < this.document.width * this.document.height) {
      const point = queue.shift()!; const key = `${point.x}:${point.y}`; if (seen.has(key)) continue; seen.add(key);
      const cell = this.document.cells[point.y * this.document.width + point.x]!; if (cell.terrain !== from) continue;
      cell.terrain = to; if (to.includes('water')) { cell.elevation = 0; cell.path = null; }
      queue.push(...neighbours(point.x, point.y, this.document.width, this.document.height));
    }
  }

  private onObject(id: string): void {
    this.renderer.highlightEntity(id, { style: 'pulse', color: '#ffe08a' });
    this.renderInspector(id); this.shell.classList.add('ifw-inspector-open');
    this.root.dispatchEvent(new CustomEvent('ifw:entity-select', { bubbles: true, detail: { id } }));
  }

  private pushHistory(): void {
    if (!this.document) return;
    this.history.push(JSON.stringify(this.document)); if (this.history.length > 30) this.history.shift(); this.future = [];
  }

  private undo(): void {
    if (!this.document || !this.history.length) return;
    this.future.push(JSON.stringify(this.document)); this.document = JSON.parse(this.history.pop()!) as MapDocument;
    this.markDirty(); void this.renderer.setWorld(this.document, this.manifest, false);
  }

  private redo(): void {
    if (!this.document || !this.future.length) return;
    this.history.push(JSON.stringify(this.document)); this.document = JSON.parse(this.future.pop()!) as MapDocument;
    this.markDirty(); void this.renderer.setWorld(this.document, this.manifest, false);
  }

  private markDirty(): void {
    if (!this.document) return;
    this.dirty = true; this.setStatus('Ungespeicherte Änderungen'); void this.drafts.save(this.document);
    clearTimeout(this.saveTimer); this.saveTimer = window.setTimeout(() => { if (this.dirty) void this.save(); }, 5000);
  }

  private async save(): Promise<void> {
    if (!this.document || !this.dirty) return;
    this.setStatus('Speichert…', true);
    try {
      const saved = this.document.revision === 0 ? await this.api.createMap(this.document) : await this.api.updateMap(this.document);
      this.document = saved; this.dirty = false; await this.drafts.remove(saved.id); this.maps = await this.api.maps(); this.updateMapSelect(); this.updatePublishButton();
      this.setStatus(`Gespeichert · Revision ${saved.revision}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) this.setStatus('Speicherkonflikt: Diese Map wurde in einem anderen Tab verändert. Bitte neu laden oder duplizieren.', false, true);
      else this.setStatus(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', false, true);
    }
  }

  private async togglePublish(): Promise<void> {
    if (!this.document) return;
    if (this.document.revision === 0) await this.save();
    if (!this.document || this.document.revision === 0) return;
    this.document = await this.api.publishMap(this.document.id, this.document.status !== 'publish'); this.updatePublishButton();
    this.setStatus(this.document.status === 'publish' ? 'Map veröffentlicht.' : 'Map ist wieder privat.');
  }

  private updatePublishButton(): void {
    const button = this.root.querySelector<HTMLButtonElement>('[data-action="publish"]'); if (!button || !this.document) return;
    button.textContent = this.document.status === 'publish' ? 'Privat stellen' : 'Veröffentlichen'; button.classList.toggle('is-published', this.document.status === 'publish');
  }

  private updateMapSelect(): void {
    const select = this.root.querySelector<HTMLSelectElement>('[data-action="map-select"]'); if (!select) return;
    select.innerHTML = `<option value="new">＋ Neue Map</option>${this.maps.map((map) => `<option value="${escapeHtml(map.id)}" ${this.document?.id === map.id ? 'selected' : ''}>${map.status === 'publish' ? '●' : '○'} ${escapeHtml(map.title)} · ${map.width}×${map.height}</option>`).join('')}`;
  }

  private setStatus(message: string, busy = false, error = false): void {
    this.status.textContent = message; this.status.classList.toggle('is-busy', busy); this.status.classList.toggle('is-error', error);
  }

  private registerPublicApi(): void {
    const publicApi: PublicWorldApi = {
      highlightEntity: (id, options) => this.renderer.highlightEntity(id, options), clearHighlight: (id) => this.renderer.clearHighlight(id),
      focusCell: (cell) => this.renderer.focusCell(cell), selectEntity: (id) => this.renderer.selectObject(id),
      moveEntity: (id, path, options) => this.renderer.moveEntity(id, path, options), getEntity: (id) => this.renderer.getEntity(id),
      getMapMetadata: () => this.renderer.getMapMetadata(),
    };
    publicInstances.set(this.root.id, publicApi);
  }
}

async function bootWorlds(): Promise<void> {
  const roots = [...document.querySelectorAll<HTMLElement>('.ifw-world')].filter((root) => !root.dataset.ifwBooted);
  await Promise.all(roots.map(async (root) => {
    root.dataset.ifwBooted = 'true';
    try {
      const bootConfig = bootConfigForRoot(root);
      if (!bootConfig) throw new Error('Die Startkonfiguration fehlt. Bitte Plugin- und Seiten-Cache leeren.');
      await new WorldApplication(root, bootConfig).init();
    }
    catch (error) {
      root.innerHTML = `<div class="ifw-fatal"><strong>Die Fantasy-Welt konnte nicht geladen werden.</strong><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></div>`;
      root.dispatchEvent(new CustomEvent('ifw:error', { bubbles: true, detail: { error } }));
    }
  }));
}

function startWorldObserver(): void {
  void bootWorlds();
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node instanceof Element && (node.matches('.ifw-world') || node.querySelector('.ifw-world'))))) {
      void bootWorlds();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startWorldObserver, { once: true }); else startWorldObserver();
